import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

interface FeatureRow {
  feature: string;
  whatItDoes: string;
  defaultProvider: string;
  defaultModel: string;
  alternatives: string;
  byok: boolean;
  sovereign: boolean;
}

const FEATURES: FeatureRow[] = [
  {
    feature: 'Intent parser',
    whatItDoes: 'Converts a natural-language test description into a structured Rynora flow.',
    defaultProvider: 'OpenAI',
    defaultModel: 'gpt-4o-mini',
    alternatives: 'Anthropic Claude 3.5 Haiku, Groq Llama-3.1-70B, Ollama (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'Healing explanation',
    whatItDoes: 'When self-healing rewrites a selector, explains in plain English what changed and why.',
    defaultProvider: 'OpenAI',
    defaultModel: 'gpt-4o-mini',
    alternatives: 'Anthropic Claude 3.5 Haiku, Ollama (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'Conversational debug',
    whatItDoes: 'Step-debugger chat — explain a failure, propose a fix, run a one-off probe.',
    defaultProvider: 'Anthropic',
    defaultModel: 'claude-3-5-sonnet',
    alternatives: 'OpenAI gpt-4o, Ollama (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'Root-cause analysis',
    whatItDoes: "Multi-step reasoning over a failed run's artefacts to suggest the most likely root cause.",
    defaultProvider: 'Anthropic',
    defaultModel: 'claude-3-5-sonnet',
    alternatives: 'OpenAI gpt-4o, vLLM (sovereign, high-throughput)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'Suggested actions / autocomplete',
    whatItDoes: 'In-editor low-latency suggestions for the next step, selector, or assertion.',
    defaultProvider: 'Groq',
    defaultModel: 'llama-3.1-8b-instant',
    alternatives: 'OpenAI gpt-4o-mini, Ollama (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'AI exploratory tester',
    whatItDoes:
      'Autonomous exploratory-testing sessions that crawl a target app, discover bugs, and write a session report.',
    defaultProvider: 'Anthropic',
    defaultModel: 'claude-3-5-sonnet',
    alternatives: 'OpenAI gpt-4o, vLLM (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'AI test-plan auditor',
    whatItDoes: 'Reads an existing test suite and grades coverage gaps + over-coverage. Used in the no-code program.',
    defaultProvider: 'OpenAI',
    defaultModel: 'gpt-4o',
    alternatives: 'Anthropic Claude 3.5 Sonnet, Ollama (sovereign)',
    byok: true,
    sovereign: true,
  },
  {
    feature: 'Privacy / compliance scanner',
    whatItDoes:
      'Detects PII (emails, phones, SSNs, card numbers, etc.) in URLs / console / network bodies / Set-Cookie.',
    defaultProvider: 'None — deterministic library, no LLM call.',
    defaultModel: '—',
    alternatives: '—',
    byok: false,
    sovereign: true,
  },
  {
    feature: 'Visual diff classifier',
    whatItDoes: 'Classifies a screenshot diff as meaningful change vs. noise (anti-aliasing, dynamic content).',
    defaultProvider: 'None — deterministic pixel-diff with optional Applitools.',
    defaultModel: '—',
    alternatives: 'Customer-side Applitools integration (BYO).',
    byok: false,
    sovereign: true,
  },
];

export function AibomPage() {
  return (
    <LegalLayout
      title="AI Bill of Materials (AIBOM)"
      updated="June 1, 2026"
      summary="Per-feature mapping of which LLM provider serves which Rynora surface, with the contractual no-training statement and our EU AI Act risk classification."
      needsReview={false}
    >
      <h2>Why this page exists</h2>
      <p>
        Buyers' AI committees increasingly require a Bill of Materials for AI features — the same way a software BOM
        lists open-source dependencies. This page is that BOM. Every Rynora surface that calls a third-party LLM is
        listed here, with the default provider, default model, the alternatives an org admin can switch to, and whether
        the surface is BYOK-capable or sovereign-capable.
      </p>

      <h2>Per-feature mapping</h2>
      <table>
        <thead>
          <tr>
            <th>Rynora feature</th>
            <th>What it does</th>
            <th>Default provider</th>
            <th>Default model</th>
            <th>Alternatives (admin-switchable)</th>
            <th>BYOK</th>
            <th>Sovereign</th>
          </tr>
        </thead>
        <tbody>
          {FEATURES.map((row) => (
            <tr key={row.feature}>
              <td>
                <strong>{row.feature}</strong>
              </td>
              <td>{row.whatItDoes}</td>
              <td>{row.defaultProvider}</td>
              <td>
                <code>{row.defaultModel}</code>
              </td>
              <td>{row.alternatives}</td>
              <td style={{ textAlign: 'center' }}>{row.byok ? 'Yes' : '—'}</td>
              <td style={{ textAlign: 'center' }}>{row.sovereign ? 'Yes' : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>"We never train on your data" — contractual statement</h2>
      <p>
        Rynora does not train any AI model on customer test flows, run artefacts, screenshots, or any other content
        derived from a customer's use of the platform. We do not aggregate customer data for model fine-tuning, do not
        sell it to data brokers, and do not allow our sub-processor LLM providers to retain or train on it.
      </p>
      <p>Specifically, every default-routed LLM call uses the provider's zero-retention / no-training mode:</p>
      <ul>
        <li>
          <strong>OpenAI</strong> — Zero Data Retention (ZDR) is enabled on our enterprise account; OpenAI does not
          retain inputs/outputs beyond the request lifetime and does not use them for training. See OpenAI's{' '}
          <a href="https://openai.com/enterprise-privacy" rel="noreferrer" target="_blank">
            Enterprise Privacy
          </a>{' '}
          page.
        </li>
        <li>
          <strong>Anthropic</strong> — API submissions are not used for training per Anthropic's commercial terms; we
          additionally request 30-day-retention deletion under the commercial agreement.
        </li>
        <li>
          <strong>Groq</strong> — Inference-only API; Groq's terms state submissions are not used for training.
        </li>
        <li>
          <strong>BYOK</strong> — When an org admin configures their own provider key (Azure OpenAI, their own Anthropic
          enterprise key, etc.), the data-handling guarantee follows their contract with that provider, not Rynora's. We
          do not see or store the prompt content.
        </li>
        <li>
          <strong>Sovereign tier</strong> — No third-party provider is involved at all. The Ollama / vLLM model runs
          inside the customer's perimeter; no prompt or response leaves the buyer's network.
        </li>
      </ul>
      <p>
        This commitment is mirrored in the Rynora Data Processing Agreement (DPA), § "AI processing", which
        contractually binds Rynora to the statement above and provides a cure-or-terminate right if violated.
      </p>

      <h2>EU AI Act classification</h2>
      <p>
        Rynora's product surface is classified as <strong>Limited Risk</strong> under the EU Artificial Intelligence Act
        (Regulation (EU) 2024/1689). The reasoning:
      </p>
      <ul>
        <li>
          Rynora does not deploy any of the eight Annex III high-risk use cases (biometric ID, critical infrastructure
          management, education access, employment decisions, essential services scoring, law enforcement, migration /
          asylum / border control, administration of justice).
        </li>
        <li>
          Rynora's AI features assist a human engineer authoring or debugging automated tests. The human is always in
          the loop: every AI-generated step is either explicitly approved (for components / shared building blocks) or
          executed only in non-production environments (test runs) and is fully revertible from version control.
        </li>
        <li>
          The transparency obligations under Art. 50 apply: end users interacting with an AI-generated explanation,
          suggestion, or test-plan audit see a clear indicator that the content was AI-generated. This is implemented
          via the (in-flight) <Link to="/handbook/feat-pending-tracker">AI output provenance</Link> badge.
        </li>
        <li>
          We do not deploy any general-purpose AI model ourselves; we are an AI <em>deployer</em> of third-party
          general-purpose AI models (OpenAI, Anthropic, Groq, Ollama, vLLM). Article 50 transparency obligations apply;
          Articles 51–55 (GPAI provider obligations) do not.
        </li>
      </ul>
      <p>
        We re-validate this classification on every material AI-feature change and at minimum quarterly. The current
        validation owner is the Rynora platform team; sign-off from outside counsel for the EU SaaS region launch is in
        scope of that workstream.
      </p>

      <h2>Hallucination and human-in-the-loop controls</h2>
      <ul>
        <li>
          <strong>No destructive automation without approval.</strong> AI-generated components (reusable building
          blocks) require human approval before they can be referenced from another flow. AI-generated steps inside a
          single flow are runnable immediately in test environments but cannot be promoted to scheduled / CI runs
          without a human commit.
        </li>
        <li>
          <strong>Visible AI provenance.</strong> Every audit-log entry that wraps an AI-originated change is tagged
          <code>ai_origin=true</code> and surfaces a visible badge in the UI. (Provenance column ships in the next
          release; see the gap list on the{' '}
          <Link to="/handbook/quality-enterprise-compliance-checklist">enterprise compliance checklist</Link>.)
        </li>
        <li>
          <strong>Per-feature opt-out.</strong> Org admins can disable any individual AI feature, or all of them, via
          Admin Settings &rarr; AI providers. End users can independently opt out of personal AI-assist features in User
          Settings.
        </li>
        <li>
          <strong>Prompt-injection mitigations.</strong> Inputs from third-party sources (target apps under test, run
          artefacts, error messages) are sanitised before being included in any prompt and are never granted tool-use
          rights. Runners cannot shell out from inside an AI-driven step. The threat model is in the handbook
          (operator-facing; available on request).
        </li>
      </ul>

      <h2>Sovereign / air-gapped option</h2>
      <p>
        For buyers whose compliance posture forbids any commercial LLM call, Rynora's{' '}
        <Link to="/sovereign-ai">sovereign tier</Link> ships a first-class Ollama provider adapter (and an optional
        higher-throughput vLLM adapter) that runs inside the buyer's VPC / on-prem subnet. Zero outbound LLM traffic.
        This is the only deployment mode in the testing-tools market that passes a FedRAMP High / IRAP / NIST 800-171 /
        HIPAA-strict / pre-IPO-fintech review.
      </p>
    </LegalLayout>
  );
}
